import { useEffect, useState } from 'react'
import { z } from 'zod'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useCustomerCpf } from '@/app/hooks/useCustomerCpf'
import { validateCpf } from '@/app/utils/cpfFormater'
import { usePanMutations } from '@/app/hooks/usePanMutations'
import { customerService } from '@/app/services/customerService'
import { transactionsService } from '@/app/services/transactionService'
import { useAuth } from '@/app/hooks/useAuth'
import { PartnerType } from '@/app/utils/partners.enum'
import { useMutation } from '@tanstack/react-query'
import { StatusEnum } from '@/app/utils/status'
import { useToast } from '@/view/components/ui/use-toast'
import { ProposalData } from '..'
import { useRegisterSupportContext } from '@/app/store/supportScreenContext'
import { journeyStore } from "@/app/services/journey"

const getPersonalInfoFormSchema = z.object({
  cpf: z
    .string()
    .min(11, "CPF é obrigatório")
    .refine((v) => validateCpf(v), "CPF Inválido")
    .transform((f) => f.replace(/\D/g, "")),
  name: z.string().min(1, "Nome é obrigatório"),
  email: z.string().min(1, "E-mail é obrigatório"),
  phone: z.string().min(1, "Telefone é obrigatório"),
});

type getPersonalInfoFormSchemaData = z.infer<typeof getPersonalInfoFormSchema>;

interface usePersonalInfoFormModalProps {
  onContinue?: (data: getPersonalInfoFormSchemaData) => void;
  onSaveProposalData: (data: ProposalData) => void;
}

export default function usePersonalInfoFormModal({
  onContinue,
  onSaveProposalData,
}: usePersonalInfoFormModalProps) {
  const getPersonalInfoForm = useForm<getPersonalInfoFormSchemaData>({
    resolver: zodResolver(getPersonalInfoFormSchema),
    mode: "onChange",
  });

  const { toast } = useToast();

  const { watch, setValue, handleSubmit } = getPersonalInfoForm;
  const { makeProposalMutation } = usePanMutations();
  const { user } = useAuth();

  const [isLoading, setIsLoading] = useState(false);
  const [isOpenConfirmModal, setIsOpenConfirmModal] = useState(false);

  const cpf = watch("cpf");
  const cpfClean = cpf?.replace(/\D/g, "");

  const {
    data: customer,
    isSuccess,
    isLoading: isLoadingCustomer,
  } = useCustomerCpf(cpfClean);

  const { mutateAsync: verifyTransactions, isPending: isLoadingTransactions } =
    useMutation({
      mutationFn: () =>
        transactionsService.transaction({
          customerIds: customer?.id,
          sellerIds: user?._id,
          type: PartnerType.PAN,
        }),
    });

  useEffect(() => {
    if (isSuccess && customer) {
      setValue("name", customer.name);
      setValue("email", customer.email);
    }
  }, [isSuccess, customer, setValue]);

  const handleVerifyTransactions = handleSubmit(async () => {
    if (customer?.id) {
      const transactions = await verifyTransactions();

      if (transactions.transactions.length > 0) {
        if (
          transactions.transactions[0].status === StatusEnum.ACTION_REQUIRED
        ) {
          return toast({
            title: "Aguardando autorização do cliente",
            variant: "default",
          });
        }

        if (transactions.transactions[0].status === StatusEnum.PENDING) {
          setIsOpenConfirmModal(true);
          return;
        }
      }
    }

    return handleSendProposal();
  });

  const handleSendProposal = async () => {
    if (isLoading || makeProposalMutation.isPending) return;

    setIsLoading(true);

    const formData = getPersonalInfoForm.getValues();
    try {
      journeyStore.start(PartnerType.PAN);

      const newCustomer = await customerService.createCustomer(
        {
          name: formData.name,
          email: formData.email,
          cpf: formData.cpf.replace(/\D/g, ""),
          phone: formData.phone.replace(/\D/g, ""),
        },
        PartnerType.PAN,
      );

      const customerId = newCustomer._id;

      const proposal = await makeProposalMutation.mutateAsync({
        customerId,
      });

      onSaveProposalData?.({
        authorizationLink: proposal.authorizationLink,
        linkExpirationDate: proposal.linkExpirationDate,
      });

      onContinue?.(formData);
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  useRegisterSupportContext('simulation.pan.personalInfo', {
    product: PartnerType.PAN,
    step: 'Dados pessoais Pan',
    data: getPersonalInfoForm.watch(),
  })

  return {
    form: getPersonalInfoForm,
    handleSendProposal,
    isOpenConfirmModal,
    setIsOpenConfirmModal,
    isLoading:
      isLoading ||
      isLoadingTransactions ||
      isLoadingCustomer ||
      makeProposalMutation.isPending,
    handleVerifyTransactions,
  };
}
